// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The route map, as data (M3-37, 08 §2).
//
// 08 §2 lists fifteen routes and most of them need something that does not exist yet: an
// identity (M4), a leaderboard (M5), a Builder (M6). This is the table of **all** of them, with
// the ones that are not here yet carrying the milestone they arrive in — because the alternative
// is worse in both directions.
//
// A route that renders "coming soon" is a page that lies: it is reachable, it looks finished
// enough to link to, and nothing ever fails when it stays that way. A destination that is simply
// missing is worse still — the navigation bar has a seat for Boards and Build in 08 §3, and a
// tab that navigates to a 404 is a broken product rather than an unfinished one.
//
// So an unbuilt destination is a **disabled control that says what it is waiting for**. That is
// a real state — the conventions require default, hover, pressed, disabled and loading on every
// control — and it is the honest one: the tab is there because the design has a seat for it, it
// does not work because the thing behind it does not exist, and it says so when you touch it.
//
// This file is `.ts` rather than `.tsx` on purpose. The table is a decision and decisions get
// tests; the components that render it are `.tsx` and get a static ARIA scan instead.

import type { NavIcon } from './navigation'
import type { MessageKey } from './i18n'

/**
 * Routes that live behind the account boundary.
 *
 * Not a permission — a **bundle**. Everything these three mount is in `src/client/account/`, which
 * `main.tsx` reaches only through `React.lazy`, so a visitor who never navigates to one downloads
 * neither the Supabase SDK nor a line of code that could open a connection (FR-030). Listed here
 * rather than inferred from the path so that adding a fourth is a decision.
 */
export const ACCOUNT_ROUTES: readonly string[] = [
  '/leaderboards',
  // The board of a machine the URL names (G3-02). The same chunk and the same page; 08 §2 has
  // listed the parameterised form since the map was written and only the bare one was mounted.
  '/leaderboards/:machineId',
  '/account',
  '/replays/:replayId',
  '/link-headset',
  // Three surfaces 08 §2 lists and nothing served (G3-06, G3-07, G3-08). All three live in the
  // account subtree because all three either need a session or reach the API — `/history` is the
  // exception that proves it, since it reads this browser's own stores and is here for weight.
  '/friends',
  '/history',
  '/profile/:handle',
  // The weekly tournament (G3-03, FR-037).
  '/tournaments',
  // The moderation queue (M9-28, 11 §6). Here for the same two reasons as the rest: it needs a
  // session and it reaches the API.
  '/moderation',
]

/**
 * Routes `main.tsx` reaches through `React.lazy`, and therefore has to wrap in a `Suspense`.
 *
 * A superset of the account ones. The Builder is behind the same mechanism for a different reason —
 * weight rather than FR-030: a canvas, its painting and its editing model have no business in the
 * bundle that draws the front door. Listed apart from `ACCOUNT_ROUTES` because that list means
 * something specific — "downloads the Supabase SDK" — and quietly widening it to mean "is lazy"
 * would make the anonymous-funnel reasoning harder to follow, not easier.
 */
//
// The machine guides are here for weight too, and of a sharper kind: their words are every
// machine's guide at once, generated at build time, and a stranger opening the front door should
// not download five pages of rules to play one machine.
export const LAZY_ROUTES: readonly string[] = [
  ...ACCOUNT_ROUTES,
  '/builder',
  '/builder/:machineId',
  '/machines/:machineId',
]

/** Does this route arrive in its own chunk, and therefore need something on screen while it does? */
export function isLazyRoute(path: string): boolean {
  return LAZY_ROUTES.includes(path)
}

export interface RouteSpec {
  /** The path, as React Router matches it. */
  readonly path: string
  /**
   * What the top bar and the document title say, as a bundle key.
   *
   * `MessageKey`, so a route added with an untranslated title does not compile. The browser
   * tab is the one piece of copy a player sees without looking at the page, and the easiest
   * to forget: nothing in the tree renders it.
   */
  readonly title: MessageKey
  /** Which navigation tab this route sits under, if any. */
  readonly tab?: NavIcon
  /**
   * The milestone that builds it, for a destination that does not exist yet.
   *
   * Absent means the route is served now. Present means the router has nothing to mount and
   * every control pointing at it renders disabled, saying this.
   */
  readonly arrives?: string
  /** Why it is not here yet, in a sentence a player could read on a disabled tab. */
  readonly waitingFor?: string
}

export const ROUTES: readonly RouteSpec[] = [
  { path: '/', title: 'route.title.play', tab: 'play' },
  { path: '/arcade', title: 'route.title.arcade', tab: 'arcade' },
  { path: '/play/:machineId', title: 'route.title.play-machine', tab: 'play' },
  // One machine's guide (S3-10): its rules, modes and playfield, in words. `tab: 'arcade'` records
  // where it belongs — the arcade is where every link to one lives — and `tabFor` is what lets the
  // bar light Arcade here, since a guide's address does not begin with `/arcade`.
  { path: '/machines/:machineId', title: 'route.title.machine-guide', tab: 'arcade' },
  { path: '/settings', title: 'route.title.settings', tab: 'you' },
  // No tab of its own: `/headset` is somewhere a player is *sent* when they ask for VR on a
  // device that cannot present it, never somewhere they navigate to. A navigation seat for it
  // would advertise a page that is meaningless to anyone already wearing a headset.
  { path: '/headset', title: 'route.title.headset' },
  { path: '/leaderboards', title: 'route.title.boards', tab: 'boards' },
  { path: '/account', title: 'route.title.account', tab: 'you' },
  { path: '/replays/:replayId', title: 'route.title.watch', tab: 'boards' },
  // No tab, for the same reason `/headset` has none and one more besides: this is the address a
  // Quest prints on its own screen (09 §4.6), so it is arrived at by being told to, never browsed
  // to. A permanent seat for it would advertise a page that is meaningless without a headset
  // already showing a code — and a security prompt is the last page that should be one tap away
  // from idle browsing.
  { path: '/link-headset', title: 'route.title.link-headset' },
  { path: '/leaderboards/:machineId', title: 'route.title.boards', tab: 'boards' },
  // Friends, history and a public page. No tab of their own: 08 §3 gives the navigation five seats
  // and these three are reached from a name — a leaderboard row, an account page, a shared link.
  // Anonymous, which is the whole reason it is not part of `/account` (G3-09). 08 §2 marks it so,
  // and a price a visitor cannot read without first making an account is an upsell this product's
  // own arcade page argues against at length.
  { path: '/pricing', title: 'route.title.pricing' },
  { path: '/friends', title: 'route.title.friends' },
  { path: '/history', title: 'route.title.history' },
  { path: '/profile/:handle', title: 'route.title.profile' },
  { path: '/tournaments', title: 'route.title.tournaments', tab: 'boards' },
  // No tab, and the reason is the third variation on the one `/headset` and `/link-headset` give.
  // Those two are meaningless unless you already have a headset in your hands; this one is
  // meaningless unless you are a moderator — and a seat that appeared only for moderators would be
  // a seat whose *absence* tells everybody else who is one. Arrived at by being told the address.
  { path: '/moderation', title: 'route.title.moderation' },
  { path: '/builder', title: 'route.title.builder', tab: 'build' },
  // Opening a machine that already exists (G1-11, 11 §7). No tab of its own: it is arrived at from
  // a machine — a roster tile, a remix, an author's own list — never browsed to, and a seat for it
  // would advertise a page that is meaningless without an id already in hand.
  { path: '/builder/:machineId', title: 'route.title.builder-machine' },
  // The six public documents (S4). No tab of their own: 08 §3 gives the navigation five seats
  // and these are reached from the footer, which is where a person looks for them and the only
  // place a crawler can follow them from. Anonymous, all of them — a privacy policy behind a
  // sign-in is a privacy policy nobody can read before deciding whether to sign in.
  { path: '/how-to-play', title: 'route.title.how-to-play' },
  { path: '/privacy', title: 'route.title.privacy' },
  { path: '/accessibility', title: 'route.title.accessibility' },
  { path: '/terms', title: 'route.title.terms' },
  // The Builder API and the MCP server (M12-18): a seventh public document, reached from the footer.
  { path: '/developers', title: 'route.title.developers' },
  // How a subscriber points their own AI at their account (M12-29). Public, because somebody
  // deciding whether to subscribe should be able to read what they would be setting up.
  { path: '/connect', title: 'route.title.connect' },
  { path: '/support', title: 'route.title.support' },
  { path: '/about', title: 'route.title.about' },
]

/** The routes the router actually mounts. */
export function servedRoutes(): readonly RouteSpec[] {
  return ROUTES.filter((route) => route.arrives === undefined)
}

/** What a control pointing at an unbuilt destination should say, or nothing when it works. */
export function unavailableReason(path: string): string | undefined {
  const route = ROUTES.find((r) => r.path === path)
  if (route === undefined || route.arrives === undefined) return undefined
  return route.waitingFor ?? `Arrives in ${route.arrives}`
}

/**
 * The title for a matched path.
 *
 * Matched against the parameterised patterns rather than by string equality, so `/play/neon-nova`
 * finds `/play/:machineId`. Longest literal prefix first, so `/` does not win everything.
 */
export function titleFor(pathname: string): MessageKey {
  let best: RouteSpec | undefined
  for (const route of ROUTES) {
    if (!matchesRoute(route.path, pathname)) continue
    if (best === undefined || route.path.length > best.path.length) best = route
  }
  return best?.title ?? 'route.title.not-found'
}

/**
 * The navigation tab a matched path sits under, or nothing.
 *
 * **The first reader `RouteSpec.tab` has had.** The field was written on eleven routes and read by
 * nothing, so the bar lit a tab only when the address began with that tab's own destination: no tab
 * on `/play/iron-fathom`, `/account`, `/tournaments` or a machine guide, although the table says
 * which each belongs to. `isTabActive` asks this as well as the prefix. Longest pattern wins, the
 * tie-break `titleFor` uses.
 */
export function tabFor(pathname: string): NavIcon | undefined {
  let best: RouteSpec | undefined
  for (const route of servedRoutes()) {
    if (!matchesRoute(route.path, pathname)) continue
    if (best === undefined || route.path.length > best.path.length) best = route
  }
  return best?.tab
}

/**
 * The two routes whose whole subject is a machine (M8-78).
 *
 * **Not a style list — a rule about what may share a screen with a cabinet.** Everything the shell
 * draws around the machine competes with it for a phone's height, and on a machine route the
 * machine wins: focus mode already drops the top bar and the navigation, `MachineStage` no longer
 * draws the getting-started strip, and this list is what tells the shell the site footer belongs to
 * the same category. A player on a phone reported "all that stuff between the bottom of the pinball
 * machine and the bottom navigation bar", and it was nine document links and a copyright line.
 *
 * `/play/:machineId` and `/` both draw `MachineStage`; every other route is a page, and a page has
 * a footer.
 */
export const MACHINE_ROUTES: readonly string[] = ['/', '/play/:machineId']

/** Is this address one of them? Pattern-matched, so `/play/iron-fathom` is one. */
export function isMachineRoute(pathname: string): boolean {
  return MACHINE_ROUTES.some((pattern) => matchesRoute(pattern, pathname))
}

/**
 * Does a route pattern match a real address?
 *
 * Exported for `src/seo/sitemap.ts`, which asks the same question for a different reason: the
 * title needs the route a path is, and the canonical link needs to know whether that route is one
 * a crawler was offered. Two copies of this would be two answers to `/play/neon-nova`.
 */
export function matchesRoute(pattern: string, pathname: string): boolean {
  const want = pattern.split('/').filter((p) => p !== '')
  const got = pathname.split('/').filter((p) => p !== '')
  if (want.length !== got.length) return false
  return want.every((segment, i) => segment.startsWith(':') || segment === got[i])
}
